"use client";

/**
 * CustomerScene — Niche v3 Detail Scene 1 ("The One Who Waits").
 *
 * Dream-aligned rewrite: adopts the same visual vocabulary as Scene 3
 * (BattlefieldScene) and Scene 4 (PathForwardScene):
 *   - 3-line dream hook stagger (counter · time anchor · amber promise)
 *   - Split persona layout (identity left / what-they-need right) — mirrors
 *     Scene 3's GiantSlideContent split + Verdict's "This works if" card
 *   - Voices rendered as numbered gate-style cards (01, 02, 03) — mirrors
 *     Scene 3's GateCard
 *   - Distribution channels list — mirrors Scene 4's Channels block
 *   - Semantic palette: emerald (what they need), red (what hurts),
 *     amber (promise), white-opacity gradations (hierarchy)
 *
 * Data mapping:
 *   niche.nicherClaim.audience      → persona role ("Indie solo devs…")
 *   niche.customerScene             → prose + time-anchor extraction
 *   niche.nicherClaim.benefit       → "what they need" hero card
 *   niche.distributionChannels[]    → channels list + audience-scale counter
 *   debate.seekerPitch.marketResearch.painEvidence → voice cards
 *
 * Reduced-motion: all stagger/spring/viewport fades skipped.
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useMemo, useState } from "react";
import { ChevronRight, ExternalLink, Globe, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { SceneHeader } from "@/components/shared-primitives/SceneHeader";
import { AnimatedCounter } from "@/components/shared-primitives/AnimatedCounter";
import { sceneHookHeadingClasses } from "@/components/shared/SceneHookHeading";
import type { NicheCandidateV3 } from "@/types/niche-v3";
import type { ParsedDebate } from "@/lib/debate-types";
import type { DistributionChannel } from "@/types";

interface CustomerSceneProps {
  niche: NicheCandidateV3;
  debate: ParsedDebate | null;
}

/* ───────── helpers ───────── */

function platformDotClass(source: string): string {
  const s = source.toLowerCase();
  if (s.includes("reddit") || s.startsWith("r/")) return "bg-emerald-400";
  if (s.includes("twitter") || s.includes("x.com") || s.startsWith("x "))
    return "bg-sky-400";
  if (s.includes("hacker") || s.includes("ycombinator") || s.includes("hn"))
    return "bg-amber-500";
  if (s.includes("linkedin")) return "bg-blue-500";
  if (s.includes("forum") || s.includes("discuss")) return "bg-purple-400";
  return "bg-white/40";
}

/** Strip "For " prefix + trailing period to get bare role phrase. */
function derivePersonaRole(niche: NicheCandidateV3): string {
  const aud = niche.nicherClaim?.audience?.trim();
  if (aud) {
    return aud.replace(/^For\s+/i, "").replace(/\.$/, "");
  }
  const scene = niche.customerScene?.trim();
  if (scene) {
    const first = scene.split(/[.!?]/)[0]?.trim();
    if (first && first.length <= 100) return first;
  }
  return "Those who wait";
}

/** Extract a short time/place anchor from prose if present. */
function deriveTimeAnchor(prose: string | undefined | null): string | null {
  if (!prose) return null;
  const patterns = [
    /\b\d{1,2}:\d{2}\s*(?:am|pm)\b/i,
    /\b(?:Mon|Tues|Wednes|Thurs|Fri|Satur|Sun)day\b/,
    /\b(?:понедельник|вторник|среда|четверг|пятница|суббота|воскресенье)\b/i,
    /\b(?:morning|evening|afternoon|night|midnight|dawn)\b/i,
    /\b(?:утро|вечер|день|ночь|полдень|полночь)\b/i,
  ];
  for (const p of patterns) {
    const m = prose.match(p);
    if (m && m[0]) return m[0];
  }
  return null;
}

/** Parse "180K", "1.2M", "45" style human numbers. */
function parseHumanNumber(s: string): number | null {
  const m = s.match(/(\d+(?:\.\d+)?)\s*([KMB])?/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (Number.isNaN(n)) return null;
  const suffix = (m[2] || "").toUpperCase();
  const mult =
    suffix === "B" ? 1e9 : suffix === "M" ? 1e6 : suffix === "K" ? 1e3 : 1;
  return Math.round(n * mult);
}

/** Sum audience count across channel notes. Null if nothing parseable. */
function deriveAudienceScale(
  channels: DistributionChannel[],
): number | null {
  let total = 0;
  let hits = 0;
  for (const c of channels) {
    if (!c.notes) continue;
    const n = parseHumanNumber(c.notes);
    if (n && n > 0) {
      total += n;
      hits++;
    }
  }
  return hits > 0 ? total : null;
}

/** Formatter for AnimatedCounter — "2.3M" / "340K" / "1,240". */
function formatHumanScale(n: number): string {
  const rounded = Math.round(n);
  if (rounded >= 1_000_000) {
    const v = rounded / 1_000_000;
    return `${v >= 10 ? v.toFixed(0) : v.toFixed(1)}M`;
  }
  if (rounded >= 1_000) {
    const v = rounded / 1_000;
    return `${v >= 10 ? v.toFixed(0) : v.toFixed(1)}K`;
  }
  return rounded.toLocaleString("en-US");
}

/** Compress a quote to <=max chars, word-boundary, ellipsis. */
function compressQuote(quote: string, max = 180): string {
  const raw = quote.replace(/^["'"']|["'"']$/g, "").trim();
  if (raw.length <= max) return raw;
  const slice = raw.slice(0, max);
  const cut = Math.max(slice.lastIndexOf(" "), max - 30);
  return `${slice.slice(0, cut).trim()}…`;
}

/* ───────── voice gate card (numbered quote) ───────── */

function VoiceGateCard({
  source,
  quote,
  date,
  url,
  index,
}: {
  source: string;
  quote: string;
  date?: string;
  url?: string;
  index: number;
}) {
  const reduced = useReducedMotion();
  const num = String(index + 1).padStart(2, "0");
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 18 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{
        duration: reduced ? 0 : 0.5,
        delay: reduced ? 0 : index * 0.1,
        ease: "easeOut",
      }}
      className="grid grid-cols-[auto_1fr] gap-6 rounded-2xl border border-white/8 bg-white/[0.02] p-6 md:gap-10 md:p-8"
    >
      {/* Large number — mirrors Scene 3 GateCard */}
      <span
        aria-hidden
        className="font-heading text-5xl font-black leading-none text-white/35 tabular-nums md:text-6xl lg:text-7xl"
      >
        {num}
      </span>
      <div className="min-w-0 [overflow-wrap:anywhere]">
        <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-white/55">
          <span
            aria-hidden
            className={cn(
              "inline-block size-2 rounded-full",
              platformDotClass(source),
            )}
          />
          <span>{source}</span>
          {date && (
            <>
              <span aria-hidden className="text-white/25">
                ·
              </span>
              <span className="tabular-nums text-white/40">{date}</span>
            </>
          )}
        </div>
        <blockquote className="font-heading italic leading-[1.5] text-white/85 text-[18px] md:text-[20px] lg:text-[22px] [overflow-wrap:anywhere]">
          &ldquo;{compressQuote(quote)}&rdquo;
        </blockquote>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-[12px] text-amber-300/80 transition-colors hover:text-amber-300 focus:outline-none focus-visible:underline"
            aria-label={`Open original post on ${source}`}
          >
            Open <ExternalLink className="size-3" aria-hidden />
          </a>
        )}
      </div>
    </motion.div>
  );
}

/* ───────── channel row — mirrors PathForwardScene channels pattern ───────── */

function ChannelRow({
  channel,
  index,
}: {
  channel: DistributionChannel;
  index: number;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.li
      initial={reduced ? false : { opacity: 0, x: -8 }}
      whileInView={reduced ? undefined : { opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{
        duration: reduced ? 0 : 0.45,
        delay: reduced ? 0 : index * 0.06,
        ease: "easeOut",
      }}
      className="flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3"
    >
      <Globe
        className="mt-0.5 size-4 shrink-0 text-white/50"
        aria-hidden
      />
      <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-heading text-[15px] font-semibold text-white/90">
            {channel.name}
          </span>
          {channel.type && (
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em] text-white/50">
              {channel.type}
            </span>
          )}
        </div>
        {channel.notes && (
          <p className="mt-0.5 text-[13px] leading-[1.45] text-white/55 [overflow-wrap:anywhere]">
            {channel.notes}
          </p>
        )}
      </div>
      {channel.url ? (
        <a
          href={channel.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-[12px] font-semibold text-amber-300 transition-colors hover:border-amber-400/60 hover:bg-amber-400/20 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/60"
        >
          Open <ExternalLink className="size-3" aria-hidden />
        </a>
      ) : (
        <Link2 aria-hidden className="size-4 shrink-0 text-white/20" />
      )}
    </motion.li>
  );
}

/* ───────── main component ───────── */

export function CustomerScene({ niche, debate }: CustomerSceneProps) {
  const reduced = useReducedMotion();
  const [expandedAll, setExpandedAll] = useState(false);

  const painQuotes = useMemo(
    () => debate?.seekerPitch?.marketResearch?.painEvidence ?? [],
    [debate],
  );
  const channels = niche.distributionChannels ?? [];

  const personaRole = useMemo(() => derivePersonaRole(niche), [niche]);
  const prose = niche.customerScene?.trim() || niche.dreamHook?.trim() || "";
  const timeAnchor = useMemo(() => deriveTimeAnchor(prose), [prose]);
  const audienceScale = useMemo(
    () => deriveAudienceScale(channels),
    [channels],
  );

  const topQuotes = painQuotes.slice(0, 3);
  const extraQuotes = painQuotes.slice(3);
  const hasMoreQuotes = extraQuotes.length > 0;

  const benefit = niche.nicherClaim?.benefit?.trim();

  const hookLineBase = sceneHookHeadingClasses({ fluid: true });

  return (
    <section
      id="scene-1-customer"
      aria-labelledby="scene-1-title"
      className="relative mx-auto w-full max-w-5xl scroll-mt-20 px-6 py-12 md:py-16 lg:px-12 lg:py-20"
    >
      <SceneHeader
        id="scene-1-title"
        number={2}
        title="The one who waits"
      />

      {/* ───────── Dream hook — 3-line stagger (mirrors BattlefieldDreamHook) ───────── */}
      <div className="flex flex-col gap-1.5 text-left">
        {/* Line 1 — counter or "Someone" */}
        <motion.h3
          initial={reduced ? false : { opacity: 0, y: 18 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: reduced ? 0 : 0.55, ease: "easeOut" }}
          className={cn(hookLineBase, "text-white")}
        >
          {audienceScale && audienceScale >= 500 ? (
            <>
              <AnimatedCounter
                to={audienceScale}
                duration={1.4}
                formatter={formatHumanScale}
                className="tabular-nums"
              />
              <span> like them.</span>
            </>
          ) : (
            <>Someone like this.</>
          )}
        </motion.h3>

        {/* Line 2 — time anchor or quiet context */}
        <motion.h3
          initial={reduced ? false : { opacity: 0, y: 18 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{
            duration: reduced ? 0 : 0.55,
            delay: reduced ? 0 : 0.2,
            ease: "easeOut",
          }}
          className={cn(hookLineBase, "text-white/55")}
        >
          {timeAnchor
            ? `It's ${timeAnchor.toLowerCase()}.`
            : "Right now."}
        </motion.h3>

        {/* Line 3 — amber gradient promise */}
        <motion.h3
          initial={reduced ? false : { opacity: 0, y: 18 }}
          whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{
            duration: reduced ? 0 : 0.55,
            delay: reduced ? 0 : 0.45,
            ease: "easeOut",
          }}
          className={cn(
            hookLineBase,
            "bg-gradient-to-r from-amber-300 via-amber-200 to-amber-100 bg-clip-text text-transparent",
          )}
        >
          Waiting for you.
        </motion.h3>
      </div>

      {/* ───────── Persona split — identity left / need right ───────── */}
      {(prose || benefit) && (
        <div className="mt-16 grid gap-8 lg:mt-20 lg:grid-cols-[1.15fr_1fr] lg:gap-10">
          {/* Identity column */}
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 16 }}
            whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: reduced ? 0 : 0.55, ease: "easeOut" }}
          >
            <div className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/45 lg:text-[12px]">
              The one who waits
            </div>
            <h4 className="mt-3 font-heading text-[28px] font-black leading-[0.95] text-white md:text-[36px] lg:text-[44px] xl:text-[52px]">
              {personaRole}
            </h4>
            {prose && (
              <p className="mt-5 font-heading text-[17px] leading-[1.6] text-white/75 md:text-[18px] lg:text-[19px]">
                {prose}
              </p>
            )}
          </motion.div>

          {/* Need column — emerald hero card (mirrors Verdict "This works if") */}
          {benefit && (
            <motion.div
              initial={reduced ? false : { opacity: 0, y: 16 }}
              whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: reduced ? 0 : 0.55,
                delay: reduced ? 0 : 0.15,
                ease: "easeOut",
              }}
              className="relative"
            >
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300/85">
                What they need ↓
              </div>
              <div className="mt-3 rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-400/[0.12] via-emerald-400/[0.06] to-transparent p-6 md:p-7 lg:p-8">
                <p className="font-heading text-[20px] font-bold leading-snug text-emerald-100 md:text-[22px] lg:text-[26px]">
                  {benefit}
                </p>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* ───────── Voices — numbered gate cards (mirror Scene 3 GateCard) ───────── */}
      {topQuotes.length > 0 && (
        <div className="mt-20">
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 10 }}
            whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: reduced ? 0 : 0.4, ease: "easeOut" }}
            className="mb-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-white/45 lg:text-[12px]"
          >
            What you’d hear them say
          </motion.div>

          <div className="space-y-4">
            {topQuotes.map((q, i) => (
              <VoiceGateCard
                key={`${q.source}-${i}`}
                source={q.source}
                quote={q.quote}
                date={q.date}
                url={q.url}
                index={i}
              />
            ))}

            <AnimatePresence initial={false}>
              {expandedAll &&
                extraQuotes.map((q, i) => (
                  <motion.div
                    key={`extra-${q.source}-${i}`}
                    initial={reduced ? false : { opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={reduced ? { opacity: 0 } : { opacity: 0, height: 0 }}
                    transition={{ duration: reduced ? 0 : 0.3 }}
                  >
                    <VoiceGateCard
                      source={q.source}
                      quote={q.quote}
                      date={q.date}
                      url={q.url}
                      index={i + 3}
                    />
                  </motion.div>
                ))}
            </AnimatePresence>
          </div>

          {hasMoreQuotes && (
            <button
              type="button"
              onClick={() => setExpandedAll((v) => !v)}
              className="mt-5 inline-flex items-center gap-1 text-[12px] font-medium uppercase tracking-[0.22em] text-white/50 transition-colors hover:text-white/80 focus:outline-none focus-visible:underline"
              aria-expanded={expandedAll}
            >
              {expandedAll
                ? "Hide extra voices"
                : `Hear all ${painQuotes.length} voices`}
              <ChevronRight
                className={cn(
                  "size-4 transition-transform",
                  expandedAll && "rotate-90",
                )}
                aria-hidden
              />
            </button>
          )}
        </div>
      )}

      {/* ───────── Channels — where they live (mirrors PathForward list) ───────── */}
      {channels.length > 0 && (
        <div className="mt-20">
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 10 }}
            whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: reduced ? 0 : 0.4, ease: "easeOut" }}
            className="mb-5 text-[11px] font-semibold uppercase tracking-[0.3em] text-white/45 lg:text-[12px]"
          >
            Where they live
          </motion.div>
          <ul className="space-y-3">
            {channels.slice(0, 6).map((c, i) => (
              <ChannelRow key={`${c.name}-${i}`} channel={c} index={i} />
            ))}
          </ul>
          <p className="mt-4 text-[12px] italic text-white/40">
            Full playbook in your Path Forward ↓
          </p>
        </div>
      )}
    </section>
  );
}
